"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/Button";
import { LocaleSwitcher } from "@/components/shell/LocaleSwitcher";
import { useFocusTrap } from "@/hooks/use-focus-trap";
import { useLocale } from "@/lib/i18n";
import { localeHref } from "@/lib/i18n/config";
import { WolfMark } from "@/components/brand/Logo";

export function Nav() {
  const [open, setOpen] = useState(false);
  const { t, locale } = useLocale();
  const menuRef = useRef<HTMLDivElement>(null);
  // trap focus inside the open mobile menu, Escape closes, focus restores to the
  // toggle on close
  useFocusTrap(menuRef, open, () => setOpen(false));

  const anchors = [
    { href: "#work", label: t.nav.work },
    { href: "#capabilities", label: t.nav.capabilities },
    { href: "#process", label: t.nav.process },
    { href: localeHref(locale, "/services"), label: t.nav.pricing },
    { href: `${localeHref(locale, "/services")}#faq`, label: t.nav.faq },
    { href: localeHref(locale, "/contact"), label: t.nav.contact },
  ];

  return (
    <>
    <header className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-[color:rgba(10,22,40,0.7)]">
      <nav
        aria-label="Primary"
        className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-[var(--spacing-page-x)]"
      >
        <Link href={localeHref(locale, "/")} className="flex items-center gap-3 group/brand">
          <WolfMark
            priority
            className="h-7 w-auto transition-transform duration-300 group-hover/brand:rotate-[6deg] group-hover/brand:scale-110"
          />
          <span
            className="font-medium tracking-[0.18em]"
            style={{
              fontFamily: "var(--font-display)",
              fontVariationSettings: '"wght" 600',
            }}
          >
            AMALBRYX
          </span>
          <span className="hidden 2xl:block h-4 w-px bg-[var(--color-hairline)]" aria-hidden />
          <span className="microcaps hidden 2xl:inline-block">{t.nav.studioSuffix}</span>
        </Link>

        <div className="hidden xl:flex items-center gap-6">
          {anchors.map((a, i) => (
            <span key={a.href} className="flex items-center gap-6">
              <Link
                href={a.href}
                className="link-underline text-sm text-[var(--color-text-dim)] transition-colors hover:text-[var(--color-text)]"
              >
                {a.label}
              </Link>
              {i < anchors.length - 1 ? (
                <span className="text-[var(--color-text-mute)]">·</span>
              ) : null}
            </span>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {/* trilingual switcher — visible from sm+ */}
          <div className="hidden sm:block">
            <LocaleSwitcher />
          </div>
          <span className="hidden md:inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-[var(--color-hairline)] px-3 py-1.5 microcaps">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--color-blue)] animate-pulse-soft"
              aria-hidden
            />
            {t.nav.available}
          </span>
          <div className="hidden md:block">
            <Button href={localeHref(locale, "/contact")} variant="ghost" trailingArrow>
              {t.nav.startProject}
            </Button>
          </div>
          <button
            type="button"
            aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden p-2 -mr-2"
          >
            <span className="block h-px w-6 bg-[var(--color-text)]" />
            <span className="block h-px w-6 bg-[var(--color-text)] mt-1.5" />
            <span className="block h-px w-6 bg-[var(--color-text)] mt-1.5" />
          </button>
        </div>
      </nav>
    </header>
    {open ? (
      <div
        ref={menuRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={t.nav.openMenu}
        style={{
          backgroundColor: "#0a1628",
          position: "fixed",
          top: "64px",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100,
        }}
        className="xl:hidden px-[var(--spacing-page-x)] pt-8 outline-none"
      >
        <ul className="flex flex-col gap-6">
          {anchors.map((a) => (
            <li key={a.href}>
              <Link
                href={a.href}
                onClick={() => setOpen(false)}
                className="block py-2 text-2xl text-[var(--color-text)]"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {a.label}
              </Link>
            </li>
          ))}
          <li className="pt-4">
            <Button
              href={localeHref(locale, "/contact")}
              variant="primary"
              trailingArrow
              onClick={() => setOpen(false)}
            >
              {t.nav.startProject}
            </Button>
          </li>
          <li className="pt-2 sm:hidden">
            <LocaleSwitcher />
          </li>
        </ul>
      </div>
    ) : null}
    </>
  );
}
